import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { buscarImoveis, obterUrlImagem } from '../../services/api';
import styles from './VitrineImoveis.module.css';

function montarTituloPublico(imovel) {
  if (imovel.endereco) {
    return `${imovel.tipo_imovel || 'Imovel'} em ${imovel.endereco}`;
  }

  return `${imovel.tipo_imovel || 'Imovel'} para ${imovel.tipo_operacao || 'negocio'}`;
}

export default function VitrineImoveis() {
  const [params, setParams] = useSearchParams();
  const [imoveis, setImoveis] = useState([]);
  const [paginacao, setPaginacao] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const termo = params.get('q') || '';
  const tipoOperacao = params.get('tipo_operacao') || '';
  const tipoImovel = params.get('tipo_imovel') || '';
  const minValor = params.get('min_valor') || '';
  const maxValor = params.get('max_valor') || '';
  const pagina = Number(params.get('pagina') || '1');
  const limite = Number(params.get('limite') || '20');

  useEffect(() => {
    const carregar = async () => {
      setCarregando(true);
      setErro('');
      try {
        const dados = await buscarImoveis({
          q: termo || undefined,
          tipo_operacao: tipoOperacao || undefined,
          tipo_imovel: tipoImovel || undefined,
          min_valor: minValor ? Number(minValor) : undefined,
          max_valor: maxValor ? Number(maxValor) : undefined,
          pagina: Number.isFinite(pagina) && pagina > 0 ? pagina : 1,
          limite: Number.isFinite(limite) && limite > 0 ? limite : 20,
        });

        setImoveis(dados.imoveis || []);
        setPaginacao(dados.paginacao || null);
      } catch (err) {
        setErro(err.message || 'Não foi possível carregar os imóveis.');
      } finally {
        setCarregando(false);
      }
    };

    carregar();
  }, [termo, tipoOperacao, tipoImovel, minValor, maxValor, pagina, limite]);

  const atualizarFiltro = (chave, valor, resetarPagina = false) => {
    const novo = new URLSearchParams(params);
    if (valor) {
      novo.set(chave, valor);
    } else {
      novo.delete(chave);
    }

    if (resetarPagina) {
      novo.set('pagina', '1');
    }

    setParams(novo);
  };

  const resumoResultados = useMemo(() => {
    if (!paginacao) return null;
    const atual = paginacao.pagina || pagina;
    const totalPaginas = paginacao.totalPaginas || 1;
    const total = paginacao.total || imoveis.length;
    return `Página ${atual} de ${totalPaginas} • ${total} imóvel(eis)`;
  }, [paginacao, pagina, imoveis.length]);

  const skeletons = Array.from({ length: 6 }, (_, i) => ({ id: `sk-${i}` }));

  return (
    <section className={styles.container}>
      <div className={`${styles.hero} ui-surface`}>
        <h1>Encontre o imóvel ideal</h1>
        <p>Busque por nome, endereço, descrição e características.</p>
      </div>

      <div className={styles.filtros}>
        <input
          className="ui-input"
          type="text"
          value={termo}
          onChange={(e) => atualizarFiltro('q', e.target.value, true)}
          placeholder="Buscar por bairro, rua, tipo..."
        />

        <select className="ui-input" value={tipoOperacao} onChange={(e) => atualizarFiltro('tipo_operacao', e.target.value, true)}>
          <option value="">Operação: todas</option>
          <option value="venda">Venda</option>
          <option value="locacao">Locação</option>
        </select>

        <select className="ui-input" value={tipoImovel} onChange={(e) => atualizarFiltro('tipo_imovel', e.target.value, true)}>
          <option value="">Tipo: todos</option>
          <option value="casa">Casa</option>
          <option value="apartamento">Apartamento</option>
          <option value="terreno">Terreno</option>
          <option value="comercial">Comercial</option>
          <option value="industrial">Industrial</option>
          <option value="rural">Rural</option>
        </select>

        <input
          className="ui-input"
          type="number"
          min="0"
          step="0.01"
          value={minValor}
          onChange={(e) => atualizarFiltro('min_valor', e.target.value, true)}
          placeholder="Valor mínimo"
        />

        <input
          className="ui-input"
          type="number"
          min="0"
          step="0.01"
          value={maxValor}
          onChange={(e) => atualizarFiltro('max_valor', e.target.value, true)}
          placeholder="Valor máximo"
        />
      </div>

      {carregando && <p className={styles.status}>Carregando imóveis...</p>}
      {erro && <p className={styles.erro}>{erro}</p>}
      {!carregando && !erro && imoveis.length === 0 && (
        <p className={styles.status}>Nenhum imóvel encontrado com os filtros atuais.</p>
      )}

      {!carregando && !erro && resumoResultados && <p className={styles.resumo}>{resumoResultados}</p>}

      <div className={styles.grid}>
        {carregando && skeletons.map((item) => (
          <article className={`${styles.card} ${styles.cardSkeleton}`} key={item.id}>
            <div className={styles.skeletonImagem} />
            <div className={styles.conteudo}>
              <div className={styles.skeletonLinhaCurta} />
              <div className={styles.skeletonLinhaMedia} />
              <div className={styles.skeletonLinhaLonga} />
              <div className={styles.skeletonLinhaLonga} />
              <div className={styles.skeletonBotao} />
            </div>
          </article>
        ))}

        {imoveis.map((imovel) => (
          <article className={`${styles.card} ui-surface`} key={imovel.id}>
            <Link to={`/imoveis/${imovel.id}`} className={styles.linkImagem}>
              <img
                src={imovel.imagemChave ? obterUrlImagem(imovel.imagemChave) : `${process.env.PUBLIC_URL}/residencial.jpg`}
                alt={montarTituloPublico(imovel)}
                className={styles.imagem}
              />
            </Link>
            <div className={styles.conteudo}>
              <p className={styles.chip}>{imovel.tipo_operacao} • {imovel.tipo_imovel}</p>
              <h2>{montarTituloPublico(imovel)}</h2>
              <p className={styles.endereco}>{imovel.endereco || 'Endereço não informado'}</p>
              <p className={styles.descricao}>{imovel.descricao || 'Sem descrição.'}</p>
              {imovel.valor && (
                <p className={styles.preco}>
                  {Number(imovel.valor).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </p>
              )}
              <Link to={`/imoveis/${imovel.id}`} className={`${styles.botao} ui-button-primary`}>
                Ver detalhes
              </Link>
            </div>
          </article>
        ))}
      </div>

      {!carregando && !erro && paginacao && (
        <div className={styles.paginacao}>
          <button
            className="ui-button-primary"
            type="button"
            onClick={() => atualizarFiltro('pagina', String(Math.max(1, pagina - 1)))}
            disabled={!paginacao.temPaginaAnterior}
          >
            Anterior
          </button>
          <span>{`Página ${paginacao.pagina} de ${paginacao.totalPaginas}`}</span>
          <button
            className="ui-button-primary"
            type="button"
            onClick={() => atualizarFiltro('pagina', String(pagina + 1))}
            disabled={!paginacao.temProximaPagina}
          >
            Próxima
          </button>
        </div>
      )}
    </section>
  );
}
